import ReplayIcon from '@mui/icons-material/Replay';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { Box, Button, Chip, FormControlLabel, IconButton, Switch, Tooltip, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import {
  FileViewerToolbarButton,
  FileViewerToolbarDivider,
  FileViewerToolbarSection,
  FileViewerToolbarText,
} from 'components/FileViewer/viewer-controls';
import { getZoomAdjustedPx } from 'components/ui/utilities/zoom';
import { useZoom } from 'context/ZoomContext';
import { PageSelectionControlsProps } from './PDFPageSelectionControls.props';
import { actionsBoxSx, PDFPageSelectionControlsContainer } from './PDFPageSelectionControls.styles';

const PDFPageSelectionControls = ({
  allowPageSelection,
  isPopped,
  selectedPages,
  onToggleSelectionMode,
  onPageToggle,
  onClearSelection,
  onProcessPages,
  genJobProcessing,
  canRetriggerCitable,
  handleRenderDocument,
  hasJsonData,
  onSearchToggle,
  searchOpen,
  zoomPercentage,
  canZoomIn,
  canZoomOut,
  canResetZoom,
  onZoomIn,
  onZoomOut,
  onResetZoom,
}: PageSelectionControlsProps) => {
  const { zoomLevel } = useZoom();
  const toolbarButtonStyle = {
    fontSize: getZoomAdjustedPx(12, zoomLevel),
    height: getZoomAdjustedPx(24, zoomLevel),
    minWidth: getZoomAdjustedPx(24, zoomLevel),
    padding: `0 ${getZoomAdjustedPx(7, zoomLevel)}`,
  };
  const toolbarIconButtonStyle = {
    ...toolbarButtonStyle,
    padding: 0,
    width: getZoomAdjustedPx(24, zoomLevel),
  };
  const toolbarIconStyle = {
    fontSize: getZoomAdjustedPx(17, zoomLevel),
  };

  return (
    <PDFPageSelectionControlsContainer $allowPageSelection={allowPageSelection} $isPopped={isPopped}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%" minWidth={0} gap={1}>
        <Stack direction="row" alignItems="center" flex={1} minWidth={0} flexWrap="wrap" gap={1}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={allowPageSelection}
                  onChange={e => onToggleSelectionMode(e.target.checked)}
                  color="primary"
                />
              }
              label="Enable Page Selection"
            />
          </Box>

          {allowPageSelection && (
            <>
              <Typography variant="body2" color="text.secondary">
                {selectedPages.size} page{selectedPages.size === 1 ? '' : 's'} selected
              </Typography>

              {selectedPages.size > 0 && (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>
                  {Array.from(selectedPages)
                    .sort((a, b) => a - b)
                    .map(pageNum => (
                      <Chip
                        key={pageNum}
                        label={`Page ${pageNum}`}
                        size="small"
                        color="primary"
                        onDelete={() => onPageToggle(pageNum)}
                        disabled={genJobProcessing}
                      />
                    ))}
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 1, ml: 'auto', flexShrink: 0, alignItems: 'center' }}>
                {selectedPages.size > 0 && (
                  <>
                    <Button variant="outlined" size="small" onClick={onClearSelection} disabled={genJobProcessing}>
                      Clear Selection
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={onProcessPages}
                      color="primary"
                      disabled={genJobProcessing}>
                      {genJobProcessing ? 'Processing...' : 'Extract Tables from Selected Pages'}
                    </Button>
                  </>
                )}
              </Box>
            </>
          )}
        </Stack>
        <Box sx={actionsBoxSx}>
          {hasJsonData && onSearchToggle && (
            <FileViewerToolbarSection
              aria-label="PDF search controls"
              style={{
                borderRadius: getZoomAdjustedPx(8, zoomLevel),
                gap: getZoomAdjustedPx(3, zoomLevel),
                minHeight: getZoomAdjustedPx(28, zoomLevel),
                padding: `${getZoomAdjustedPx(2, zoomLevel)} ${getZoomAdjustedPx(4, zoomLevel)}`,
              }}>
              <FileViewerToolbarButton
                aria-label={searchOpen ? 'Close PDF search' : 'Search in PDF'}
                aria-pressed={searchOpen}
                data-testid="pdf-search-toggle"
                onClick={onSearchToggle}
                style={toolbarIconButtonStyle}
                type="button">
                <SearchOutlinedIcon style={toolbarIconStyle} />
              </FileViewerToolbarButton>
            </FileViewerToolbarSection>
          )}
          <FileViewerToolbarSection
            aria-label={allowPageSelection ? 'PDF page selection zoom controls' : 'PDF zoom controls'}
            style={{
              borderRadius: getZoomAdjustedPx(8, zoomLevel),
              gap: getZoomAdjustedPx(3, zoomLevel),
              minHeight: getZoomAdjustedPx(28, zoomLevel),
              padding: `${getZoomAdjustedPx(2, zoomLevel)} ${getZoomAdjustedPx(4, zoomLevel)}`,
            }}>
            <FileViewerToolbarButton
              aria-label="Zoom out"
              disabled={!canZoomOut}
              onClick={onZoomOut}
              style={toolbarButtonStyle}
              type="button">
              −
            </FileViewerToolbarButton>
            <FileViewerToolbarText
              style={{
                fontSize: getZoomAdjustedPx(11, zoomLevel),
                minWidth: getZoomAdjustedPx(42, zoomLevel),
                padding: `0 ${getZoomAdjustedPx(3, zoomLevel)}`,
                textAlign: 'center',
              }}>
              {zoomPercentage}%
            </FileViewerToolbarText>
            <FileViewerToolbarButton
              aria-label="Zoom in"
              disabled={!canZoomIn}
              onClick={onZoomIn}
              style={toolbarButtonStyle}
              type="button">
              +
            </FileViewerToolbarButton>
            <FileViewerToolbarDivider
              aria-hidden="true"
              style={{
                height: getZoomAdjustedPx(14, zoomLevel),
                margin: `0 ${getZoomAdjustedPx(2, zoomLevel)}`,
              }}
            />
            <FileViewerToolbarButton
              aria-label="Fit PDF to preview width"
              disabled={!canResetZoom}
              onClick={onResetZoom}
              style={toolbarButtonStyle}
              type="button">
              Fit
            </FileViewerToolbarButton>
          </FileViewerToolbarSection>

          {canRetriggerCitable && (
            <Tooltip title="Re-run citable parsing">
              <IconButton size="small" onClick={handleRenderDocument}>
                <ReplayIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Stack>
    </PDFPageSelectionControlsContainer>
  );
};

export default PDFPageSelectionControls;
